import { useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CalendarPlus, FileText, Paperclip, Send } from 'lucide-react';
import { SURVEY_STATUS, dateTimeLabel, type SurveyStatus } from '@adisys/shared';
import { api, ApiRequestError, fileObjectUrl } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { MetaBadge, Notice, Section, errorText, useCrmMeta, useOpenFile, type TabProps } from '../../../lib/crm';
import {
  Button, EmptyState, ErrorState, Field, Input, Modal, Select, Skeleton, Textarea, useToast,
} from '../../../components/ui';

const FIELDS = [
  ['siteDetails', 'Site details', 'Building, floors, access, power, existing cabling…'],
  ['customerRequirements', 'Customer requirements', 'What the customer wants to achieve'],
  ['productNeeds', 'Product needs', 'Kinds and rough quantities of equipment'],
  ['technicalRequirements', 'Technical requirements', 'Resolution, retention, bandwidth, standards…'],
  ['measurements', 'Measurements', 'Areas, cable runs, mounting heights'],
  ['reportSummary', 'Report summary', 'Overall findings and recommendations'],
] as const;
type FieldKey = (typeof FIELDS)[number][0];

function Thumb({ fileId, title, onOpen }: { fileId: string; title: string; onOpen: () => void }) {
  const q = useQuery({ queryKey: ['crm', 'thumb', fileId], queryFn: () => fileObjectUrl(fileId), staleTime: 600_000 });
  return (
    <button onClick={onOpen} title={title}
      className="h-20 w-20 overflow-hidden rounded-lg bg-ink-100 ring-1 ring-line hover:ring-ink-400">
      {q.data ? <img src={q.data} alt={title} className="h-full w-full object-cover" />
        : <Skeleton className="h-full w-full" />}
    </button>
  );
}

function SurveyCard({ survey, project, reload, canManage, writable }: {
  survey: any; project: any; reload: () => void; canManage: boolean; writable: boolean;
}) {
  const toast = useToast();
  const { user, can } = useAuth();
  const open = useOpenFile();
  const fileRef = useRef<HTMLInputElement>(null);
  const [category, setCategory] = useState<'site_photo' | 'survey_report'>('site_photo');
  const [values, setValues] = useState<Record<FieldKey, string>>(
    () => Object.fromEntries(FIELDS.map(([k]) => [k, survey[k] ?? ''])) as Record<FieldKey, string>);
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const locked = survey.status === 'submitted' || survey.status === 'cancelled';
  const mine = survey.assignedTo === user?.id || can('crm.lead.view.all', 'crm.stage.override');
  const editable = canManage && writable && !locked && mine;
  const refetch = reload;

  const save = useMutation({
    mutationFn: () => api.patch(`/crm/surveys/${survey.id}`, Object.fromEntries(FIELDS.map(([k]) => [k, values[k].trim() || null]))),
    onSuccess: () => { setDirty(false); toast.success('Survey saved'); refetch(); },
    onError: (e) => toast.error('Could not save the survey', errorText(e)),
  });

  const submit = useMutation({
    mutationFn: async () => {
      if (dirty) await api.patch(`/crm/surveys/${survey.id}`, Object.fromEntries(FIELDS.map(([k]) => [k, values[k].trim() || null])));
      return api.post(`/crm/surveys/${survey.id}/submit`);
    },
    onSuccess: () => { toast.success('Survey submitted', 'The technical team can now prepare the BOQ.'); setDirty(false); refetch(); },
    onError: (e) => {
      if (e instanceof ApiRequestError && e.details) {
        setErrors(Object.fromEntries(Object.entries(e.details).map(([k, v]) => [k, v[0] ?? ''])));
      }
      toast.error('The survey is not ready to submit', errorText(e));
    },
  });

  const cancel = useMutation({
    mutationFn: () => api.post(`/crm/surveys/${survey.id}/cancel`, { reason: cancelReason }),
    onSuccess: () => { setCancelOpen(false); toast.success('Survey cancelled'); refetch(); },
    onError: (e) => toast.error('Could not cancel', errorText(e)),
  });

  const upload = useMutation({
    mutationFn: (files: FileList) => {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append('files', f));
      fd.append('entityType', 'survey'); fd.append('entityId', survey.id); fd.append('category', category);
      return api.upload('/crm/documents', fd);
    },
    onSuccess: () => { toast.success('Files attached'); refetch(); },
    onError: (e) => toast.error('Upload failed', errorText(e)),
  });

  return (
    <div className="space-y-4 rounded-lg p-4 ring-1 ring-line">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <MetaBadge meta={SURVEY_STATUS} value={survey.status as SurveyStatus} />
          <span className="text-sm text-ink-700">{survey.assignedToName} · {dateTimeLabel(survey.scheduledAt)}</span>
        </div>
        {survey.submittedAt && <span className="text-xs text-ink-500">Submitted {dateTimeLabel(survey.submittedAt)} by {survey.submittedByName}</span>}
      </div>

      {canManage && writable && !locked && !mine && (
        <Notice>Only {survey.assignedToName} (or a sales manager) can record findings for this survey.</Notice>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        {FIELDS.map(([k, label, hint]) => (
          <Field key={k} label={label} error={errors[k]} className={k === 'reportSummary' ? 'md:col-span-2' : undefined}>
            <Textarea rows={3} value={values[k]} placeholder={hint} disabled={!editable} invalid={Boolean(errors[k])}
              onChange={(e) => { setValues((v) => ({ ...v, [k]: e.target.value })); setDirty(true); setErrors((x) => ({ ...x, [k]: '' })); }} />
          </Field>
        ))}
      </div>

      <div className="space-y-2">
        <p className="text-xs font-medium text-ink-700">Photos and reports</p>
        {survey.documents.length === 0 ? <p className="text-xs text-ink-500">Nothing attached yet.</p> : (
          <div className="flex flex-wrap gap-2">
            {survey.documents.map((d: any) => d.mimeType?.startsWith('image/')
              ? <Thumb key={d.id} fileId={d.fileId} title={d.title ?? 'Photo'} onOpen={() => void open(d.fileId)} />
              : (
                <button key={d.id} onClick={() => void open(d.fileId)}
                  className="flex h-20 w-28 flex-col items-center justify-center gap-1 rounded-lg bg-ink-50 px-2 text-center text-xs text-ink-700 ring-1 ring-line hover:ring-ink-400">
                  <FileText className="h-5 w-5 text-ink-400" aria-hidden /><span className="line-clamp-2">{d.title ?? 'Document'}</span>
                </button>
              ))}
          </div>
        )}
        {editable && (
          <div className="flex flex-wrap items-center gap-2">
            <Select value={category} onChange={(e) => setCategory(e.target.value as typeof category)} className="w-44" aria-label="Attachment type">
              <option value="site_photo">Site photo</option><option value="survey_report">Survey report</option>
            </Select>
            <input ref={fileRef} type="file" multiple accept="image/*,application/pdf" className="hidden"
              onChange={(e) => { if (e.target.files?.length) upload.mutate(e.target.files); e.target.value = ''; }} />
            <Button size="sm" icon={<Paperclip className="h-3.5 w-3.5" />} loading={upload.isPending} onClick={() => fileRef.current?.click()}>
              Attach files
            </Button>
            <span className="text-xs text-ink-500">JPG, PNG, WEBP, HEIC or PDF, up to 10 MB each</span>
          </div>
        )}
      </div>

      {editable && (
        <div className="flex flex-wrap justify-end gap-2 border-t border-line pt-3">
          <Button variant="ghost" onClick={() => { setCancelReason(''); setCancelOpen(true); }}>Cancel survey</Button>
          <Button loading={save.isPending} disabled={!dirty} onClick={() => save.mutate()}>Save draft</Button>
          <Button variant="primary" icon={<Send className="h-4 w-4" />} loading={submit.isPending} onClick={() => submit.mutate()}>
            Submit to technical team
          </Button>
        </div>
      )}

      <Modal open={cancelOpen} onClose={() => setCancelOpen(false)} title="Cancel this survey" size="sm"
        footer={<>
          <Button onClick={() => setCancelOpen(false)}>Back</Button>
          <Button variant="danger" loading={cancel.isPending} disabled={cancelReason.trim().length < 3} onClick={() => cancel.mutate()}>Cancel survey</Button>
        </>}>
        <Field label="Reason" required>
          <Textarea rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} maxLength={300} />
        </Field>
      </Modal>
    </div>
  );
}

export function SurveyTab({ project, reload }: TabProps) {
  const { can } = useAuth();
  const toast = useToast();
  const meta = useCrmMeta();
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [when, setWhen] = useState('');
  const [who, setWho] = useState('');
  const [err, setErr] = useState<Record<string, string>>({});

  const q = useQuery({ queryKey: ['crm', 'project', project.id, 'surveys'], queryFn: () => api.get(`/crm/projects/${project.id}/surveys`) });

  const canManage = can('crm.survey.manage');
  const active = project.status === 'active';
  const stageOk = ['site_survey', 'boq'].includes(project.stage) || can('crm.stage.override');
  const writable = active && stageOk;

  const schedule = useMutation({
    mutationFn: () => api.post(`/crm/projects/${project.id}/surveys`, { scheduledAt: when, assignedTo: who }),
    onSuccess: () => { toast.success('Survey scheduled'); setScheduleOpen(false); void q.refetch(); reload(); },
    onError: (e) => {
      if (e instanceof ApiRequestError && e.details) setErr(Object.fromEntries(Object.entries(e.details).map(([k, v]) => [k, v[0] ?? ''])));
      else toast.error('Could not schedule', errorText(e));
    },
  });

  const surveys: any[] = q.data?.data ?? [];
  // Child cards refresh the list through the parent's reload; keep the list in step too.
  const wrappedReload = () => { void q.refetch(); reload(); };

  return (
    <Section title="Site survey" description="Visit the site, record what you find, and hand it to the technical team."
      action={canManage && (
        <Button icon={<CalendarPlus className="h-4 w-4" />} disabled={!writable}
          onClick={() => { setWhen(''); setWho(''); setErr({}); setScheduleOpen(true); }}>Schedule survey</Button>
      )}>
      {!canManage && <Notice>You can view surveys here, but scheduling and recording findings need survey permission.</Notice>}
      {canManage && !active && <Notice tone="warning">This project is {project.status.replace('_', ' ')}, so surveys cannot change.</Notice>}
      {canManage && active && !stageOk && <Notice>Surveys are handled before the BOQ is priced. This project has moved past that point.</Notice>}

      {q.isLoading ? <Skeleton className="h-48" />
        : q.isError ? <ErrorState error={q.error} onRetry={() => q.refetch()} />
        : surveys.length === 0 ? <EmptyState title="No survey yet" description="Schedule a site visit to capture requirements." />
        : <div className="space-y-4">{surveys.map((s) => (
            <SurveyCard key={`${s.id}-${s.status}-${s.submittedAt ?? ''}`} survey={s} project={project} reload={wrappedReload}
              canManage={canManage} writable={writable} />))}</div>}

      <Modal open={scheduleOpen} onClose={() => setScheduleOpen(false)} title="Schedule a site survey" size="sm"
        footer={<>
          <Button onClick={() => setScheduleOpen(false)}>Back</Button>
          <Button variant="primary" loading={schedule.isPending} disabled={!when || !who} onClick={() => schedule.mutate()}>Schedule</Button>
        </>}>
        <div className="space-y-3">
          <Field label="Date and time" required error={err.scheduledAt}>
            <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} invalid={Boolean(err.scheduledAt)} />
          </Field>
          <Field label="Surveyor" required error={err.assignedTo}>
            <Select value={who} onChange={(e) => setWho(e.target.value)} invalid={Boolean(err.assignedTo)}>
              <option value="">Choose…</option>
              {(meta.data?.people?.surveyors ?? []).map((u: any) => <option key={u.id} value={u.id}>{u.fullName}</option>)}
            </Select>
          </Field>
        </div>
      </Modal>
    </Section>
  );
}
